import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, User } from "lucide-react";

// Beast V1 Checkpoint 4 — + New Job dialog.
// Approved minimal interaction: Customer → Title → (optional Due Date) → Create.
// Commercial mode snapshot is derived server-side from the Customer.
// On success we invoke `onCreated(job)` so the caller can immediately
// open the Job detail drawer.
export const NewJobDialog = ({ onClose, onCreated }) => {
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/customers")
      .then((r) => {
        if (!alive) return;
        setCustomers(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (alive) setCustomers([]);
      })
      .finally(() => {
        if (alive) setLoadingCustomers(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = customerFilter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => (c.display_name || "").toLowerCase().includes(q));
  }, [customers, customerFilter]);

  const canSubmit = !!customerId && !!title.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        title: title.trim(),
        description: "",
        origin_ref: { ref_type: "manual", ref_id: "" },
        due_at: dueAt || "",
      };
      const { data } = await api.post("/admin/jobs", payload);
      toast.success("Job created");
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="new-job-dialog"
      className="fixed inset-0 z-[9998] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border border-[#262626] rounded-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">New Job</h3>
          <button
            data-testid="new-job-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-[#262626] grid place-items-center hover:border-[#FF6600]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="text-xs text-[#A3A3A3] block mb-3">
          Customer
          <input
            data-testid="new-job-customer-filter"
            className="ur-input mt-1 w-full"
            placeholder={loadingCustomers ? "Loading customers…" : "Type to filter customers…"}
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            disabled={loadingCustomers}
          />
        </label>

        <div
          data-testid="new-job-customer-list"
          className="max-h-40 overflow-y-auto border border-[#1F1F1F] rounded-lg mb-4 divide-y divide-[#1F1F1F]"
        >
          {loadingCustomers ? (
            <div className="p-3 text-xs text-[#A3A3A3] flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-xs text-[#A3A3A3]">
              {customers.length === 0
                ? "No customers yet. Create one in Operations → Customers."
                : "No matches."}
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`new-job-customer-option-${c.id}`}
                onClick={() => setCustomerId(c.id)}
                className={`w-full flex items-center gap-3 p-2.5 text-left text-sm transition-colors ${
                  customerId === c.id ? "bg-[#FF6600]/15" : "hover:bg-[#1A1A1A]"
                }`}
              >
                <User className="w-4 h-4 text-[#5A5A5A]" />
                <span className="flex-1 truncate">{c.display_name}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                  {c.commercial_mode || "standard"}
                </span>
              </button>
            ))
          )}
        </div>

        <label className="text-xs text-[#A3A3A3] block mb-3">
          Job Title
          <input
            data-testid="new-job-title"
            className="ur-input mt-1 w-full"
            placeholder="e.g. Cookie Castle spring campaign"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="text-xs text-[#A3A3A3] block mb-5">
          Due Date (optional)
          <input
            data-testid="new-job-due-at"
            type="date"
            className="ur-input mt-1 w-full"
            value={dueAt ? dueAt.slice(0, 10) : ""}
            onChange={(e) => setDueAt(e.target.value ? `${e.target.value}T00:00:00Z` : "")}
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            data-testid="new-job-cancel"
            onClick={onClose}
            className="ur-btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="new-job-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="ur-btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create Job
          </button>
        </div>
      </div>
    </div>
  );
};
